import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ClipboardList } from "lucide-react";
import { getProduct } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker } from "@/components/Reveal";

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const { add } = useQuote();

  useEffect(() => {
    setData(null);
    setError(false);
    setActiveImg(0);
    getProduct(slug).then(setData).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="px-5 pb-32 pt-48 text-center" data-testid="product-not-found">
        <p className="font-display text-4xl">Product not found.</p>
        <Link to="/shop" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-kwila">
          <ArrowLeft size={15} /> Back to shop
        </Link>
      </div>
    );

  if (!data)
    return (
      <div className="px-5 pb-32 pt-48 text-center text-sm text-sand" data-testid="product-loading">
        Loading product…
      </div>
    );

  const { product: p, related } = data;
  const gallery = p.gallery?.length
    ? p.gallery
    : [p.image, `${p.image}&sat=-25`, `${p.image}&crop=entropy`];

  return (
    <div data-testid="product-detail-page">
      <section className="px-5 pb-24 pt-28 md:px-10 md:pt-36">
        <div className="mx-auto max-w-[1500px]">
          <Link
            to={`/shop?division=${p.division}`}
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-sand transition-colors hover:text-kwila"
          >
            <ArrowLeft size={14} /> {p.division}
          </Link>

          <div className="mt-8 grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="overflow-hidden rounded-sm bg-stone" data-testid="product-gallery-main">
                <img src={gallery[activeImg]} alt={`${p.name} — ${p.species}`} className={`aspect-[4/5] w-full md:aspect-[5/5] ${gallery[activeImg].startsWith("/") ? "object-contain p-10" : "object-cover"}`} />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4" data-testid="product-gallery-thumbs">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`overflow-hidden rounded-sm bg-stone transition-shadow ${activeImg === i ? "ring-2 ring-kwila" : "ring-1 ring-transparent hover:ring-ink/25"}`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={src} alt={`${p.name} view ${i + 1}`} loading="lazy" className={`aspect-square w-full transition-transform duration-700 hover:scale-105 ${src.startsWith("/") ? "object-contain p-4" : "object-cover"}`} />
                  </button>
                ))}
              </div>
            </Reveal>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <Reveal>
                  <Kicker>{p.division} · {p.category}</Kicker>
                  <h1 className="mt-3 font-display text-4xl font-light leading-none tracking-tight md:text-6xl" data-testid="product-name">
                    {p.name}
                  </h1>
                  <p className="mt-3 font-display text-xl italic text-kwila" data-testid="product-price-note">
                    {p.price_note}
                  </p>
                  <p className="mt-5 text-base leading-relaxed text-ink/70">{p.description}</p>

                  <ul className="mt-6 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-ink/75">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-kwila" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <button
                      data-testid="product-add-to-quote-button"
                      onClick={() => add(p)}
                      className="flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
                    >
                      <ClipboardList size={15} /> Add to Quote List
                    </button>
                    <Link
                      to="/request-quote"
                      data-testid="product-request-quote-link"
                      className="flex items-center gap-2 rounded-full border border-ink/30 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-ink hover:text-cream"
                    >
                      Request a Quote <ArrowRight size={14} />
                    </Link>
                  </div>

                  <div className="mt-10 divide-y divide-ink/10 border-y border-ink/10" data-testid="product-specs">
                    {[
                      ["Timber species", p.species],
                      ["Dimensions", p.dimensions],
                      ["Finish", p.finish],
                      ["Availability", p.availability],
                      ["Lead time", p.lead_time],
                      ["Delivery", p.delivery],
                      ["Warranty", p.warranty],
                    ].map(([k, v]) => (
                      <div key={k} className="grid grid-cols-3 gap-4 py-3.5 text-sm">
                        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-sand">{k}</span>
                        <span className="col-span-2 text-ink/80">{v}</span>
                      </div>
                    ))}
                  </div>

                  <p className="mt-6 text-xs leading-relaxed text-ink/55">
                    New Kwila can release natural tannins when wet — read our{" "}
                    <Link to="/guides/tannin-bleed" data-testid="product-tannin-link" className="font-bold text-kwila underline underline-offset-2">
                      tannin bleed guide
                    </Link>{" "}
                    and{" "}
                    <Link to="/guides/kwila-care" data-testid="product-care-link" className="font-bold text-kwila underline underline-offset-2">
                      care guide
                    </Link>{" "}
                    before positioning.
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-stone/60 px-5 py-24 md:px-10" data-testid="related-products">
          <div className="mx-auto max-w-[1500px]">
            <Reveal className="mb-10">
              <Kicker>Keep Browsing</Kicker>
              <h2 className="mt-3 font-display text-3xl font-light tracking-tight md:text-5xl">Related products</h2>
            </Reveal>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r, i) => (
                <ProductCard key={r.id} product={r} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <QuoteCTA />
    </div>
  );
}
